import { useSetPageTitle } from 'app/shared/context-hooks/use-titlebar.hook';
import styled from 'styled-components/macro';
import Heading from 'app/shared/components/typography/heading/Heading';
import styledFuncs from 'app/styles/styledFuncs';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
} from '@material-ui/core';
import {
  useGetRepairerHistory,
  useTransactionDetail,
} from 'app/core/api/repairer/api-repairer';
import AppImage from 'app/shared/components/app-image/AppImage';
import { DefaultUserImg } from 'app/shared/components/misc';
import {
  DiagnosticsQuote,
  Quote,
  TransactionQuote,
  QuoteDetails as RepairQuoteDetails,
} from 'app/core/api/repairer/api-repairer.types';
import Icon from 'app/shared/components/icon/Icon';
import IconLib from 'app/shared/components/icon/icon-lib';
import { useShowInfo } from 'app/shared/context-hooks/use-show-info.hook';
import { useEffectOnce } from 'app/tools/hooks/misc-hooks';
import { PageDetails } from 'app/core/api/company/api-company.types';
import { useSetRepairersActivity } from 'app/core/api/company/api-company';

const LinkHeader = styled(Heading)`
  color: ${p => p.theme.palette.primary.main};
  text-align: left !important;
  font-size: ${styledFuncs.pxToRem(16)};
  margin-left: ${styledFuncs.pad(0.5)};
  font-weight: normal;
`;

const PriceRight = styled.div`
  position: absolute;
  right: ${styledFuncs.pad(2)};
  top: ${styledFuncs.pad(4)};
  font-size: 20px;
  color: #31ad31;
  font-weight: normal;
`;

const StyledAccordion = styled(Accordion)`
  border-radius: 10px;
`;
const InfoBox = styled.div`
  margin-left: ${styledFuncs.pad(0.5)};
  margin-right: ${styledFuncs.pad(0.5)};
`;
const CenteredBox = styled.div`
  flex-grow: 1;
  flex-direction: column;
  display: flex;
  justify-content: center;
  text-align: center;
  > * {
    margin: ${styledFuncs.pad(0.5)} 0;
  }
`;
const Table = styled.table`
  border-collapse: separate;
  table-layout: fixed;
  th {
    text-align: left;
    padding: ${styledFuncs.pad(0.5)} ${styledFuncs.pad(1.5)};
  }
  th:nth-child(2) {
    text-align: center;
  }
  th:nth-child(3) {
    text-align: right;
  }
  tr {
    border-radius: 10px;
  }
  td {
    border: 1px solid #eaeaea;
    padding: ${styledFuncs.pad(0.5)} ${styledFuncs.pad(1.5)};
  }
  td:first-child {
    border-radius: 10px 0 0 10px;
    width: 40%;
  }
  td:nth-child(2) {
    text-align: center;
  }
  td:last-child {
    border-radius: 0 10px 10px 0;
    text-align: right;
  }
  tr:last-child td {
    font-weight: 600;
    color: #31ad31;
  }
  thead {
    display: table;
    table-layout: fixed;
    width: 100%;
  }
  tbody {
    display: table;
    table-layout: fixed;
    width: 100%;
    border-spacing: 0 10px;
  }
`;

export default function History(): JSX.Element {
  useSetPageTitle('History');
  const { request$: repairActivity } = useSetRepairersActivity();
  useEffectOnce(() => {
    const pageDetails: PageDetails = {
      pagename: 'History',
    };
    repairActivity(pageDetails).subscribe();
  });
  const { resource: historyData } = useGetRepairerHistory();
  const {
    resource: repairerTransaction,
    request: loadTransactionDetail$,
  } = useTransactionDetail();
  const { showDialog } = useShowInfo();

  const DirectToDetails = (id: number) => {
    loadTransactionDetail$(id);
  };

  const handleInformationRequest = () =>
    showDialog({
      title: 'Platform Fee',
      message:
        'This fee is equal to 10% of the subtotal (excluding spare parts charges and courier charges)',
      actions: [{ text: 'Okay!' }],
    });

  const foundTransaction = (
    <>
      {historyData &&
        historyData.map((val: TransactionQuote) => (
          <StyledAccordion color="default" key={val.id}>
            <AccordionSummary onClick={() => DirectToDetails(val.id)}>
              <AppImage
                alt="profile image"
                src={val.imageUrl}
                fallbackImgNode={<DefaultUserImg size={60} />}
                height="60px"
                noPad
                className="overflow-hidden w-10 h-10 rounded shrink-0"
              />
              <PriceRight>£{val.total}</PriceRight>
              <InfoBox>
                <b>{val.productName}</b>
                <br />
                <text>{val.type}</text>
                <br />
                <text>{val.consumerName}</text>
                <br />
                <text>{val.date} &nbsp; &nbsp;</text>
                <text>{val.time}</text>
              </InfoBox>
            </AccordionSummary>
            <AccordionDetails>
              <Table>
                <thead>
                  <tr>
                    <th>Description</th>
                    <th>Qty</th>
                    <th>Price</th>
                  </tr>
                </thead>
                <tbody>
                  {repairerTransaction &&
                    repairerTransaction[0]?.diagnosisQuote.map(
                      (dq: DiagnosticsQuote) => (
                        <tr key={dq.quotationAmount.amount}>
                          <td>Diagnosis Quote</td>
                          <td>
                            {
                              repairerTransaction[0].diagnosisQuote
                                .length
                            }
                          </td>
                          <td>£{dq.quotationAmount.amount}</td>
                        </tr>
                      )
                    )}
                  {repairerTransaction &&
                    repairerTransaction[0]?.repairQuote.map(
                      (rq: Quote) =>
                        rq.quoteDetails ? (
                          rq.quoteDetails.map(
                            (rdq: RepairQuoteDetails, rdqIndex) => (
                              // new Date().toString()
                              <tr key={`repair${rdqIndex + 1}`}>
                                <td>
                                  {rdq.description && rdq.description}
                                  {!rdq.description && 'Repair Quote'}
                                </td>
                                <td>{rdq.qty}</td>
                                <td>
                                  {rdq.qty > 1 && (
                                    <span
                                      style={{
                                        color: '#a8a8a8',
                                        float: 'left',
                                      }}
                                    >
                                      (£{rdq.total})
                                    </span>
                                  )}
                                  <span style={{ color: '#000000' }}>
                                    £
                                    {Number.isInteger(
                                      rdq.qty * rdq.total
                                    ) && rdq.qty * rdq.total}
                                    {!Number.isInteger(
                                      rdq.qty * rdq.total
                                    ) &&
                                      (rdq.qty * rdq.total).toFixed(
                                        2
                                      )}
                                  </span>
                                </td>
                              </tr>
                            )
                          )
                        ) : (
                          <tr key={rq.quotationAmount.amount}>
                            <td>{val.repairCaseDescription}</td>
                            <td>
                              {
                                repairerTransaction[0].repairQuote
                                  .length
                              }
                            </td>
                            <td>£{rq.quotationAmount.amount}</td>
                          </tr>
                        )
                    )}
                  <tr>
                    <td>Subtotal</td>
                    <td />
                    <td>£{val.subtotal}</td>
                  </tr>
                  <tr>
                    <td>
                      Platform Fee
                      <Icon
                        icon={IconLib.INFO}
                        size={14}
                        color="#31ad31"
                        onClick={() => handleInformationRequest()}
                      />
                    </td>
                    <td />
                    <td>-£{val.applicationFee}</td>
                  </tr>
                  <tr>
                    <td>Total</td>
                    <td />
                    <td>£{val.total}</td>
                  </tr>
                </tbody>
              </Table>
              {/* <QuoteDetails quoteSummery={val.id} /> */}
            </AccordionDetails>
          </StyledAccordion>
        ))}
    </>
  );

  const noTransactions = (
    <CenteredBox>
      <text>
        Nothing to see here yet - this is where you'll find
        transactions with repairers when you've completed a payment
      </text>
    </CenteredBox>
  );
  return (
    <>
      <LinkHeader>Recent Transactions</LinkHeader>
      {historyData?.length ? foundTransaction : noTransactions}
    </>
  );
}
